import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  PencilSimpleIcon,
  TrashIcon,
  DownloadSimpleIcon,
  DotsThreeOutlineVerticalIcon,
} from '@phosphor-icons/react';
import { EventAttendanceContent } from '@/content/EventAttendanceContent';
import type { EventActionButtonProps } from '@/lib/types/attendance.types';

export interface EventActionsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onExport?: () => void;
  isDeleting?: boolean;
}

export function EventActionsDrawer({
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onExport,
  isDeleting = false,
}: EventActionsDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <button
          type="button"
          aria-label={EventAttendanceContent.buttons.eventActionsTrigger}
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-slate-50 text-slate-50 transition hover:border-purple-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-purple-400"
        >
          <DotsThreeOutlineVerticalIcon size={25} />
        </button>
      </DrawerTrigger>

      <DrawerContent>
        <div className="w-full ">
          <DrawerHeader>
            <DrawerTitle>
              {EventAttendanceContent.drawer.actionsTitle}
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              {'Attendance actions for this event.'}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-row gap-2">
            <EventActionButton
              label={EventAttendanceContent.buttons.editEvent}
              className="bg-blue-400"
              icon={<PencilSimpleIcon size={28} weight="regular" />}
              onClick={onEdit}
            />
            <EventActionButton
              label={EventAttendanceContent.buttons.deleteEvent}
              className="bg-red-500"
              icon={<TrashIcon size={28} weight="regular" />}
              onClick={onDelete}
              disabled={isDeleting}
            />
            <EventActionButton
              label={EventAttendanceContent.buttons.exportEvent}
              className="bg-green-400"
              icon={<DownloadSimpleIcon size={28} />}
              onClick={onExport}
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function EventActionButton({
  label,
  className,
  icon,
  onClick,
  disabled,
}: EventActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col w-full items-center rounded-md px-5 py-6 ${className} ${
        disabled ? 'opacity-60 cursor-not-allowed' : ''
      }`}
    >
      <span className="flex w-full items-center justify-center text-slate-50">
        {icon}
      </span>
      <span className="text-large">{label}</span>
    </button>
  );
}

